// ---------------------------------------------------------------------------
// The pie clock, for a screen with a keyboard over half of it.
//
// `PieClock` is the clock. This is the same reading when there is no room for a
// disc: a phone with its keyboard up has 340 to 440 pixels of height left, and a
// 128px disc is a third of it — the one thing on a play screen that can be made
// small without taking anything away, because the seconds are the reading and
// the wedge angle is a bar drawn straight instead of round.
//
// It is a swap of PRESENTATION and never of state. The same four variants, the
// same colours as `PieClock`'s wedge, the same `data-clock-variant` for anything
// that watches, and the label is required here for the same reason it is there:
// there are no numerals inside a disc, and there is nothing but numerals here,
// so the sentence a screen reader gets has to be the same either way.
//
// The track is an opaque `raised` fill rather than a wash. A translucent track
// inside a tinted card composites two washes deep, which is off the bottom of
// what the palette was solved for — the same rule the chart bars follow.
// ---------------------------------------------------------------------------

import type { ReactElement } from 'react';
import type { ClockVariant } from './PieClock.js';

/** The wedge colours, matching `WEDGE_COLOR` in PieClock. One clock, two shapes. */
const FILL: Record<ClockVariant, string> = {
  normal: 'var(--c-blue-400)',
  alert: 'var(--c-alert)',
  buzz: 'var(--c-accent)',
  frozen: 'var(--c-clock-frozen)',
};

export interface CompactClockProps {
  /** 0 to 1, the same fraction the disc draws. Clamped internally. */
  remaining: number;
  /** Whole seconds left. This is the readout, so here it is on the screen. */
  seconds: number;
  variant?: ClockVariant;
  /** Required, exactly as on `PieClock`: the numerals alone are not a reading. */
  label: string;
  className?: string;
}

export function CompactClock({
  remaining,
  seconds,
  variant = 'normal',
  label,
  className,
}: CompactClockProps): ReactElement {
  const fraction = Number.isFinite(remaining) ? Math.min(1, Math.max(0, remaining)) : 0;
  const fill = FILL[variant];

  return (
    <div
      role="img"
      aria-label={label}
      data-testid="compact-clock"
      data-clock-variant={variant}
      className={`flex min-w-0 flex-1 items-center gap-[10px] transition-opacity duration-500 ease-out ${
        className ?? ''
      }`}
    >
      {/* Fixed width and tabular, so a clock going from 10 to 9 does not drag
          the bar beside it one character to the left. */}
      <span
        className="tabular w-[26px] shrink-0 text-right font-mono text-[20px] leading-none font-bold"
        style={{ color: fill }}
      >
        {seconds}
      </span>
      <span className="h-[6px] min-w-[40px] flex-1 overflow-hidden rounded-full bg-raised">
        <span
          className="block h-full rounded-full transition-[width] duration-300 ease-linear"
          style={{ width: `${fraction * 100}%`, background: fill }}
        />
      </span>
    </div>
  );
}
